import { useEffect, useRef, useState } from 'react';
import { useRealTimeChat } from '../../../chat/context/RealTimeChatContext';
import {
    isAlreadyGenerated,
    isDocumentProgress,
    readProgressPercent,
} from '../utils/documentProgress';

const CLOSE_DELAY_MS = 500;
const DEFAULT_START_MESSAGE = 'Preparing document...';
const DEFAULT_READY_MESSAGE = 'Document ready! Starting download...';

/**
 * Follows the server's progress messages for one document download.
 *
 * The progress dialog stays closed at first (the caller shows a spinner on its button). It opens
 * only when the server really starts generating, and closes shortly after 100%. If the document
 * already exists, it never opens: `onReady` is called with the server's message instead.
 *
 * @param {{ rfpId: string, isActive: boolean, onReady: (message: string) => void }} options
 *        isActive is true while a download is running; other messages are ignored
 * @returns {{ showProgress: boolean, percent: number, message: string }}
 */
export const useDocumentProgress = ({ rfpId, isActive, onReady }) => {
    const { subscribeToMessages } = useRealTimeChat();
    const [showProgress, setShowProgress] = useState(false);
    const [percent, setPercent] = useState(0);
    const [message, setMessage] = useState(DEFAULT_START_MESSAGE);

    // The handler reads these, so it is subscribed once and never reads stale values.
    const isActiveRef = useRef(isActive);
    const onReadyRef = useRef(onReady);
    const showProgressRef = useRef(false);
    const readyShownRef = useRef(false);
    const closeTimerRef = useRef(null);

    useEffect(() => { onReadyRef.current = onReady; }, [onReady]);

    useEffect(() => {
        isActiveRef.current = isActive;
        clearTimeout(closeTimerRef.current);
        readyShownRef.current = false;
        showProgressRef.current = false;
        setShowProgress(false);
        setPercent(0);
        setMessage(DEFAULT_START_MESSAGE);
    }, [isActive]);

    useEffect(() => {
        const closeSoon = () => {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = setTimeout(() => {
                showProgressRef.current = false;
                setShowProgress(false);
            }, CLOSE_DELAY_MS);
        };

        const unsubscribe = subscribeToMessages((payload) => {
            if (!isDocumentProgress(payload)) return;
            if (String(payload.rfp_id) !== String(rfpId)) return;
            if (!isActiveRef.current) return;

            if (isAlreadyGenerated(payload)) {
                if (showProgressRef.current) {
                    // A real generation just finished: show 100% briefly, then close.
                    setPercent(100);
                    closeSoon();
                }
                if (!readyShownRef.current) {
                    readyShownRef.current = true;
                    if (onReadyRef.current) onReadyRef.current(payload.message || DEFAULT_READY_MESSAGE);
                }
                return;
            }

            const nextPercent = readProgressPercent(payload);
            if (payload.message) setMessage(payload.message);
            setPercent(nextPercent);

            if (nextPercent >= 100) {
                if (showProgressRef.current) closeSoon();
                return;
            }
            if (!showProgressRef.current) {
                showProgressRef.current = true;
                setShowProgress(true);
            }
        });

        return () => {
            unsubscribe();
            clearTimeout(closeTimerRef.current);
        };
    }, [subscribeToMessages, rfpId]);

    return { showProgress, percent, message };
};
